import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import { Upload, PenTool, CheckCircle2, ArrowRight, Loader2, RotateCcw } from "lucide-react";
import { Nav, Footer } from "@/components/Nav";
import { getBooking, uploadLicense, uploadCfPhoto, completeBooking } from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";

const MANLEVA_TEXT = `Io sottoscritto/a, con la firma della presente, dichiaro di partecipare all'esperienza di guida in pista organizzata da VELORA JDM Experience in piena autonomia e sotto la mia esclusiva responsabilità.

Dichiaro di:
- essere maggiorenne e in possesso di patente di guida valida ed efficace;
- essere in buono stato di salute fisica e psichica, senza controindicazioni all'attività di guida sportiva;
- aver preso visione del regolamento del circuito e delle norme di sicurezza illustrate durante il briefing;
- accettare che l'attività si svolga su tracciato chiuso al traffico e che comporti rischi tipici della guida sportiva.

Manlevo VELORA JDM Experience, gli organizzatori, gli accompagnatori del team Velora e i partner tecnici da ogni responsabilità per eventuali danni a persone o cose derivanti dalla mia partecipazione, salvo quanto previsto dalla legge in caso di dolo o colpa grave.

Accetto che la durata del turno di guida sia variabile ad insindacabile giudizio degli organizzatori e possa essere interrotta immediatamente qualora si evidenzino condotte di guida pericolose, non conformi al briefing o alle indicazioni dell'accompagnatore del team Velora, senza diritto a rimborso, nemmeno parziale, né prolungamento del turno.

RESPONSABILITÀ PER DANNI. Mi impegno a condurre il veicolo con la diligenza richiesta dalle circostanze, seguendo scrupolosamente le indicazioni dell'accompagnatore del team Velora e il briefing di sicurezza. Sono consapevole che eventuali danni al veicolo o alle strutture del circuito riconducibili a mia condotta gravemente colposa o dolosa (mancato rispetto del briefing, comportamenti volontariamente pericolosi, uso improprio dei comandi) restano a mio carico nei limiti previsti dalla legge. VELORA non richiede alcuna cauzione né franchigia al momento della prenotazione: la copertura ordinaria dei rischi è a carico dell'Organizzatore.

Autorizzo il trattamento dei miei dati personali ai sensi del Reg. UE 2016/679 (GDPR) per le finalità legate all'evento e ai relativi obblighi contrattuali, fiscali e assicurativi.`;

const CONDIZIONI_TEXT = `TERMINI E CONDIZIONI VELORA JDM EXPERIENCE — riepilogo essenziale

1. OGGETTO. VELORA JDM Experience è un'esperienza di guida sportiva su tracciato chiuso al traffico (Circuito del Sele, Battipaglia). Ogni pacchetto include uno o più turni di guida al volante o come passeggero, con obbligatoria presenza a bordo di un accompagnatore certificato del team Velora.

2. REQUISITI DRIVER. Il Cliente che guida dichiara di essere maggiorenne, in possesso di patente valida per la categoria del veicolo (cat. B minimo), in piena idoneità fisica e psichica, e di non essere sotto l'effetto di alcol, stupefacenti o farmaci che compromettano la guida.

3. DURATA TURNO. La durata del turno di guida è variabile ad insindacabile giudizio degli organizzatori e può essere interrotta immediatamente per condotte pericolose o non conformi al briefing, senza rimborso né prolungamento.

4. PREZZI E VOUCHER. Il pagamento si perfeziona tramite Stripe. Al completamento del pagamento viene emesso un voucher digitale nominativo con validità 12 mesi.

5. RECESSO. Ai sensi dell'art. 59 comma 1 lett. n) D.Lgs. 206/2005 il recesso di 14 giorni NON si applica alle esperienze con data specifica. Vale la Politica Rimborsi VELORA (voucher riprogrammabile 12 mesi, rimborso monetario ≥30gg).

6. RESPONSABILITÀ. Attività su tracciato chiuso con presenza di accompagnatore. Rischi intrinseci della guida sportiva consapevolmente accettati. VELORA risponde entro i limiti di dolo e colpa grave (artt. 1229 e 1341 c.c.). L'accesso pista è subordinato al completamento della modulistica entro 48h prima dell'evento.

7. FORZA MAGGIORE. In caso di impossibilità di erogazione per meteo/ordinanze/chiusura circuito è prevista riprogrammazione automatica gratuita o rimborso a scelta del Cliente.

8. FORO. Contratto regolato dalla legge italiana. Foro del consumatore competente in via esclusiva ai sensi art. 66-bis Codice del Consumo.

Testo completo su /termini.`;

const PRIVACY_TEXT = `INFORMATIVA PRIVACY — riepilogo essenziale (artt. 13-14 Reg. UE 2016/679 GDPR)

1. TITOLARE. I dati identificativi completi del titolare del trattamento sono comunicati al momento del pagamento tramite Stripe e riportati su ricevuta/fattura. Contatto privacy: privacy@velorajdm.com.

2. DATI TRATTATI. Anagrafica, contatti, codice fiscale, copia patente, firma manleva, dati fiscali (se richiesta fattura), immagini foto/video (previo consenso), log tecnici.

3. FINALITÀ E BASI GIURIDICHE.
- Esecuzione contratto e voucher (art. 6.1.b) — 24 mesi
- Fatturazione elettronica (art. 6.1.c obbligo legge) — 10 anni
- Verifica patente accesso pista (art. 6.1.b + 6.1.c) — 30gg post-evento
- Manleva firmata per tutela legale (art. 6.1.f) — 5 anni
- Foto/video promozionali (art. 6.1.a consenso) — fino a revoca
- Newsletter marketing (art. 6.1.a consenso) — fino a revoca

4. DESTINATARI. Stripe Payments Europe (processore pagamenti), provider hosting, consulenti fiscali/legali, autorità pubbliche su richiesta, compagnia assicurativa in caso di sinistro. Nessuna cessione a terzi per finalità commerciali proprie.

5. TRASFERIMENTI EXTRA-UE. Stripe può trattare dati negli USA sulla base delle Standard Contractual Clauses UE e del Data Privacy Framework.

6. DIRITTI (artt. 15-22 GDPR). Accesso, rettifica, cancellazione, limitazione, portabilità, opposizione, revoca consenso, reclamo al Garante (gpdp.it). Contatto privacy@velorajdm.com — risposta entro 30 giorni.

7. COOKIE. Solo cookie tecnici (sessione, checkout Stripe). Nessun cookie di profilazione o pubblicitario.

Testo completo su /privacy.`;

export default function Tunnel() {
    const { bookingId } = useParams();
    const [sp] = useSearchParams();
    const bookingToken = sp.get("t") || "";
    const sid = sp.get("sid") || "";
    const navigate = useNavigate();
    const [booking, setBooking] = useState(null);
    const [step, setStep] = useState(1);
    const [uploading, setUploading] = useState(false);
    const [uploadingCf, setUploadingCf] = useState(false);
    const [checks, setChecks] = useState({ maggiorenne: false, patente: false, idoneita: false, gdpr: false, immagini: false, whatsapp_marketing: false });
    const [reads, setReads] = useState({ privacy: false, condizioni: false, manleva: false });
    const [signatureName, setSignatureName] = useState("");
    const [fiscal, setFiscal] = useState({
        codice_fiscale: "", telefono: "+39 ",
        nato_a: "", nato_il: "",
        residenza_via: "", residenza_citta: "", residenza_cap: "", residenza_provincia: "",
    });
    const [wantsInvoice, setWantsInvoice] = useState(true);
    const [invoice, setInvoice] = useState({ ragione_sociale: "", partita_iva: "", codice_sdi: "", pec: "" });
    const [preferenza, setPreferenza] = useState("Indifferente");
    const [preferenzaAuto, setPreferenzaAuto] = useState("Indifferente");
    const [submitting, setSubmitting] = useState(false);
    const [loadError, setLoadError] = useState(null); // V53.5 · gestione errore caricamento
    const sigRef = useRef(null);

    useEffect(() => {
        getBooking(bookingId, bookingToken)
            .then(b => { setBooking(b); setSignatureName(b.customer_name || ""); if (b.has_license) setStep(2); if (b.manleva_signed) setStep(4); })
            .catch(err => {
                const code = err?.response?.status;
                let msg;
                if (code === 403) msg = { title: "Link non valido o scaduto", detail: "Il link ricevuto per email è vecchio. Scrivici su WhatsApp: ti rimandiamo subito il link nuovo funzionante (in pochi minuti).", waMsg: `Ciao, sono ${bookingId ? "ID " + bookingId.slice(0, 8) : "un cliente"} e il link check-in che ho ricevuto non funziona. Puoi rimandarmelo per favore?` };
                else if (code === 404) msg = { title: "Prenotazione non trovata", detail: "Non riusciamo a trovare la tua prenotazione. Se hai un dubbio, scrivici.", waMsg: "Ciao, non trovate la mia prenotazione dal link ricevuto." };
                else msg = { title: "Impossibile caricare la prenotazione", detail: "C'è stato un problema temporaneo. Ricarica la pagina o scrivici su WhatsApp.", waMsg: "Ciao, ho un problema con il tunnel check-in della mia prenotazione." };
                setLoadError(msg);
                toast.error(msg.title);
            });
    }, [bookingId, bookingToken]);

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        // Serve almeno CF scritto OPPURE foto CF prima di caricare la patente
        const hasCfWritten = fiscal.codice_fiscale && fiscal.codice_fiscale.trim().length >= 11;
        const hasCfPhoto = !!booking?.has_cf_photo;
        if (!hasCfWritten && !hasCfPhoto) {
            toast.error("Prima inserisci il codice fiscale (16 caratteri) oppure caricane la foto");
            e.target.value = "";
            return;
        }
        setUploading(true);
        try {
            await uploadLicense(bookingId, f, bookingToken);
            toast.success("Patente caricata");
            const b = await getBooking(bookingId, bookingToken);
            setBooking(b);
            setStep(2);
        } catch (err) { toast.error(err?.response?.data?.detail || "Errore upload"); }
        finally { setUploading(false); }
    };

    const onCfPhoto = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploadingCf(true);
        try {
            await uploadCfPhoto(bookingId, f, bookingToken);
            toast.success("Foto codice fiscale caricata");
            const b = await getBooking(bookingId, bookingToken);
            setBooking(b);
        } catch (err) { toast.error(err?.response?.data?.detail || "Errore upload"); }
        finally { setUploadingCf(false); }
    };

    // Valida numero telefono: prefisso internazionale + almeno 6 cifre dopo
    const validatePhone = (raw) => {
        const p = (raw || "").trim();
        // Formato accettato: "+XX XXXXXXXX" — prefisso 1-4 cifre, poi almeno 6 cifre (spazi/trattini ok)
        return /^\+\d{1,4}[\s\-]?\d[\d\s\-]{6,15}$/.test(p);
    };

    // signMode: 'digital' (default, one-click) OR 'autograph' (canvas)
    const [signMode, setSignMode] = useState("digital");

    const submit = async () => {
        if (!Object.values(checks).slice(0, 4).every(Boolean)) return toast.error("Spunta le dichiarazioni obbligatorie");
        const hasCfWritten = fiscal.codice_fiscale.trim().length >= 11;
        const hasCfPhoto = !!booking?.has_cf_photo;
        if (!hasCfWritten && !hasCfPhoto) return toast.error("Serve il codice fiscale (scritto o foto)");
        if (!validatePhone(fiscal.telefono)) return toast.error("Numero WhatsApp non valido — deve iniziare con prefisso internazionale (es. +39)");
        if (!checks.whatsapp_marketing) return toast.error("Serve il consenso WhatsApp per completare");
        if (wantsInvoice && !invoice.partita_iva.trim() && !invoice.codice_sdi.trim()) return toast.error("Per la fattura serve P.IVA o Codice SDI");

        let dataurl = null;
        if (signMode === "autograph") {
            if (!sigRef.current || sigRef.current.isEmpty()) return toast.error("Firma richiesta");
            dataurl = sigRef.current.getCanvas().toDataURL("image/png");
        }
        setSubmitting(true);
        try {
            await completeBooking(bookingId, {
                dichiarazione_maggiorenne: checks.maggiorenne,
                dichiarazione_patente: checks.patente,
                dichiarazione_idoneita: checks.idoneita,
                consenso_gdpr: checks.gdpr,
                consenso_immagini: checks.immagini,
                consenso_whatsapp_marketing: checks.whatsapp_marketing,
                signature_type: signMode,  // "digital" | "autograph"
                signature_dataurl: dataurl,
                signature_name: signatureName || booking.customer_name,
                codice_fiscale: fiscal.codice_fiscale.toUpperCase(),
                telefono: fiscal.telefono.trim(),
                nato_a: fiscal.nato_a,
                nato_il: fiscal.nato_il,
                residenza_via: fiscal.residenza_via,
                residenza_citta: fiscal.residenza_citta,
                residenza_cap: fiscal.residenza_cap,
                residenza_provincia: fiscal.residenza_provincia,
                richiede_fattura: wantsInvoice,
                ragione_sociale: invoice.ragione_sociale,
                partita_iva: invoice.partita_iva,
                codice_sdi: invoice.codice_sdi,
                pec: invoice.pec,
                preferenza_fascia: preferenza,
                preferenza_auto: preferenzaAuto,
            }, bookingToken);
            toast.success("Manleva completata!");
            setStep(4);
        } catch (err) { toast.error(err?.response?.data?.detail || "Errore invio"); }
        finally { setSubmitting(false); }
    };

    if (loadError) return (
        <div className="min-h-screen bg-[#0f1116] p-6 flex items-center justify-center" data-testid="tunnel-error">
            <div className="max-w-md w-full border border-[#E2231A]/40 bg-[#1C2026] p-6 text-center">
                <div className="text-[#E2231A] text-4xl mb-3">⚠️</div>
                <h1 className="text-[#F4F2ED] text-lg uppercase tracking-widest mb-2">{loadError.title}</h1>
                <p className="text-[#9AA2AD] text-sm mb-4">{loadError.detail}</p>
                <div className="flex gap-2 justify-center">
                    <a href={`https://wa.me/393274803439?text=${encodeURIComponent(loadError.waMsg || "Ciao, ho un problema con il check-in")}`} target="_blank" rel="noopener noreferrer" data-testid="tunnel-error-whatsapp"
                        className="border border-[#25D366]/60 hover:bg-[#25D366]/10 text-[#25D366] px-4 py-2 text-xs uppercase tracking-widest">
                        💬 Richiedi nuovo link
                    </a>
                    <button onClick={() => window.location.reload()} data-testid="tunnel-error-retry"
                        className="border border-[#4B7BEA]/60 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-4 py-2 text-xs uppercase tracking-widest">
                        ↻ Riprova
                    </button>
                </div>
            </div>
        </div>
    );
    if (!booking) return (
        <div className="min-h-screen bg-[#0f1116] flex items-center justify-center" data-testid="tunnel-loading">
            <div className="text-center">
                <div className="inline-block w-8 h-8 border-2 border-[#E2231A] border-t-transparent rounded-full animate-spin mb-3"></div>
                <div className="text-[#9AA2AD] uppercase tracking-widest text-xs">Caricamento prenotazione…</div>
            </div>
        </div>
    );
    const progress = step === 1 ? 20 : step === 2 ? 55 : step === 3 ? 85 : 100;

    return (
        <div className="min-h-screen bg-[#15181C] text-[#F4F2ED]">
            <Nav />
            <div className="max-w-3xl mx-auto px-5 md:px-8 py-10">
                <div className="text-xs uppercase tracking-[0.3em] text-[#9AA2AD] mb-2">Voucher {booking.voucher_code}</div>
                <h1 className="font-display text-5xl uppercase mb-2">Perfeziona<br/><span className="text-[#E2231A]">l'accesso.</span></h1>
                <p className="text-sm text-[#9AA2AD] uppercase tracking-widest mb-4">Prosegui con il check-in guidato — 3 step, meno di 5 minuti.</p>
                <Progress data-testid="tunnel-progress" value={progress} className="h-1 bg-[#1C2026] mb-8" />

                {/* Step 1: License + Codice Fiscale (fattura-ready) */}
                <Step num="01" title="Documenti di identità" active={step === 1} done={!!booking.patente_url}>
                    <p className="text-sm text-[#9AA2AD] mb-4">Patente <b className="text-[#F4F2ED]">obbligatoria</b> + codice fiscale (uno tra scrittura o foto).</p>

                    {/* CF — testo OR foto (almeno uno dei due) */}
                    <div className="border border-[#9AA2AD]/20 bg-[#15181C] p-4 mb-4">
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-3">Codice fiscale — <b className="text-[#F4F2ED]">scrivilo</b> oppure <b className="text-[#F4F2ED]">carica una foto</b> della tessera</div>
                        <label className="block text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">Scrivi il tuo codice fiscale</label>
                        <input
                            data-testid="input-cf-step1"
                            value={fiscal.codice_fiscale}
                            onChange={e => setFiscal(f => ({ ...f, codice_fiscale: e.target.value.toUpperCase() }))}
                            placeholder="RSSMRA85M01H501Z"
                            maxLength={16}
                            className="w-full bg-[#1C2026] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 uppercase tracking-widest text-sm font-mono"
                        />
                        <div className="flex items-center gap-3 my-3">
                            <span className="flex-1 h-px bg-[#9AA2AD]/20" />
                            <span className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">oppure</span>
                            <span className="flex-1 h-px bg-[#9AA2AD]/20" />
                        </div>
                        <label className="block text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">Foto tessera sanitaria / CF</label>
                        {booking.has_cf_photo ? (
                            <div className="flex items-center gap-3 border border-[#E2231A]/40 bg-[#E2231A]/5 p-3 text-sm">
                                <CheckCircle2 className="text-[#E2231A]" size={18} />
                                Foto CF caricata correttamente.
                            </div>
                        ) : (
                            <label className="cursor-pointer inline-flex items-center gap-3 border border-[#9AA2AD]/40 hover:border-[#E2231A] px-4 py-3 transition-colors duration-150 text-sm">
                                {uploadingCf ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} strokeWidth={1.5} />}
                                <span className="uppercase tracking-widest">{uploadingCf ? "Caricamento…" : "Carica foto CF (opzionale)"}</span>
                                <input data-testid="cf-photo-upload" type="file" accept="image/*,application/pdf" onChange={onCfPhoto} className="hidden" />
                            </label>
                        )}
                    </div>

                    {/* Upload patente — obbligatoria */}
                    <div className="border border-[#9AA2AD]/20 bg-[#15181C] p-4">
                        <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-1">Patente di guida <b className="text-[#E2231A]">*obbligatoria</b></div>
                        <p className="text-xs text-[#9AA2AD] mb-3">Fronte e retro in un'unica foto o scansione (JPG/PNG/PDF, max 8MB).</p>
                        {booking.patente_url ? (
                            <div className="flex items-center gap-3 border border-[#E2231A]/40 bg-[#E2231A]/5 p-4">
                                <CheckCircle2 className="text-[#E2231A]" />
                                <div className="text-sm">Patente caricata correttamente.</div>
                            </div>
                        ) : (
                            <label className="cursor-pointer inline-flex items-center gap-3 border border-[#9AA2AD]/40 hover:border-[#E2231A] px-5 py-4 transition-colors duration-150">
                                {uploading ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} strokeWidth={1.5} />}
                                <span className="uppercase tracking-widest text-sm">{uploading ? "Caricamento…" : "Seleziona file patente"}</span>
                                <input data-testid="license-upload" type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" />
                            </label>
                        )}
                    </div>
                </Step>

                {/* Step 2: Read & accept legal texts — Privacy, Condizioni, Manleva */}
                <Step num="02" title="Leggi e accetta Privacy, Condizioni e Manleva" active={step === 2} done={step > 2}>
                    <p className="text-sm text-[#9AA2AD] mb-4">Scorri e leggi ciascun documento. Devi accettare tutti e tre per proseguire.</p>

                    <LegalBox
                        tid="box-privacy"
                        title="Informativa Privacy"
                        subtitle="GDPR — trattamento dei tuoi dati personali"
                        text={PRIVACY_TEXT}
                        fullLink="/privacy"
                        accepted={reads.privacy}
                        onAccept={(v) => setReads(r => ({ ...r, privacy: !!v }))}
                        acceptLabel="Ho letto e accetto l'informativa Privacy"
                    />
                    <LegalBox
                        tid="box-condizioni"
                        title="Termini e Condizioni"
                        subtitle="Contratto di servizio VELORA JDM"
                        text={CONDIZIONI_TEXT}
                        fullLink="/termini"
                        accepted={reads.condizioni}
                        onAccept={(v) => setReads(r => ({ ...r, condizioni: !!v }))}
                        acceptLabel="Ho letto e accetto Termini e Condizioni"
                    />
                    <LegalBox
                        tid="box-manleva"
                        title="Manleva di Responsabilità"
                        subtitle="Dichiarazione da firmare — obbligatoria per l'accesso pista"
                        text={MANLEVA_TEXT}
                        accepted={reads.manleva}
                        onAccept={(v) => setReads(r => ({ ...r, manleva: !!v }))}
                        acceptLabel="Ho letto e accetto la Manleva"
                    />

                    <button
                        data-testid="btn-read-manleva"
                        onClick={() => setStep(3)}
                        disabled={!(reads.privacy && reads.condizioni && reads.manleva)}
                        className="mt-2 border border-[#E2231A] hover:bg-[#E2231A]/10 disabled:opacity-30 disabled:cursor-not-allowed px-5 py-3 uppercase tracking-widest text-sm transition-colors duration-150 inline-flex items-center gap-2"
                    >
                        {(reads.privacy && reads.condizioni && reads.manleva) ? "Ho letto tutto, continua" : "Accetta tutti i documenti per proseguire"} <ArrowRight size={16} />
                    </button>
                </Step>

                {/* Step 3: Sign + Fiscal */}
                {step >= 3 && (
                <Step num="03" title="Dichiarazioni, dati fiscali & firma" active={step === 3} done={step > 3}>
                    <div className="mb-5">
                        <div className="text-xs uppercase tracking-[0.3em] text-[#9AA2AD] mb-2">Dati anagrafici (obbligatori per accesso pista)</div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <input data-testid="input-cf" value={fiscal.codice_fiscale} onChange={e => setFiscal(f => ({ ...f, codice_fiscale: e.target.value.toUpperCase() }))} placeholder="Codice fiscale *" maxLength={16} className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 uppercase tracking-widest text-sm" />
                            <div className="flex flex-col">
                                <input
                                    data-testid="input-telefono"
                                    value={fiscal.telefono}
                                    onChange={e => setFiscal(f => ({ ...f, telefono: e.target.value }))}
                                    placeholder="+39 327 480 3439"
                                    inputMode="tel"
                                    autoComplete="tel"
                                    className={`bg-[#15181C] border ${fiscal.telefono && !validatePhone(fiscal.telefono) ? "border-[#E2231A]" : "border-[#9AA2AD]/30"} focus:border-[#E2231A] outline-none px-4 py-3 text-sm`}
                                />
                                <span className="text-[9px] uppercase tracking-widest text-[#9AA2AD] mt-1">📱 WhatsApp / cellulare * — con prefisso internazionale</span>
                            </div>
                            <input value={fiscal.nato_a} onChange={e => setFiscal(f => ({ ...f, nato_a: e.target.value }))} placeholder="Nato a" className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 text-sm" />
                            <input value={fiscal.nato_il} onChange={e => setFiscal(f => ({ ...f, nato_il: e.target.value }))} placeholder="Nato il (gg/mm/aaaa)" className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 text-sm" />
                            <input value={fiscal.residenza_via} onChange={e => setFiscal(f => ({ ...f, residenza_via: e.target.value }))} placeholder="Via e civico" className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 text-sm md:col-span-2" />
                            <input value={fiscal.residenza_citta} onChange={e => setFiscal(f => ({ ...f, residenza_citta: e.target.value }))} placeholder="Città" className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 text-sm" />
                            <div className="grid grid-cols-2 gap-3">
                                <input value={fiscal.residenza_cap} onChange={e => setFiscal(f => ({ ...f, residenza_cap: e.target.value }))} placeholder="CAP" maxLength={5} className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 text-sm" />
                                <input value={fiscal.residenza_provincia} onChange={e => setFiscal(f => ({ ...f, residenza_provincia: e.target.value.toUpperCase() }))} placeholder="Prov." maxLength={2} className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 uppercase text-sm" />
                            </div>
                        </div>
                    </div>

                    <div className="mb-5">
                        <div className="text-xs uppercase tracking-[0.3em] text-[#9AA2AD] mb-2">Preferenza fascia oraria (indicativa)</div>
                        <div className="flex gap-2 flex-wrap">
                            {["Mattina", "Pomeriggio", "Indifferente"].map(p => (
                                <button key={p} type="button" data-testid={`fascia-${p}`} onClick={() => setPreferenza(p)} className={`px-4 py-2 border uppercase tracking-widest text-xs transition-colors duration-150 ${preferenza === p ? "border-[#E2231A] bg-[#E2231A]/10 text-[#F4F2ED]" : "border-[#9AA2AD]/30 text-[#9AA2AD] hover:text-[#F4F2ED]"}`}>{p}</button>
                            ))}
                        </div>
                        <p className="text-[10px] uppercase tracking-widest text-[#9AA2AD]/70 mt-2">Sistema a numero progressivo — orari comunicati via email/SMS.</p>
                    </div>

                    {booking.product_id === "passenger_experience" && (
                        <div className="mb-5">
                            <div className="text-xs uppercase tracking-[0.3em] text-[#9AA2AD] mb-2">Preferenza auto (Passeggero)</div>
                            <div className="flex gap-2 flex-wrap">
                                {[
                                    { k: "EVO", label: "EVO X", color: "#E2231A" },
                                    { k: "WRX", label: "WRX", color: "#4B7BEA" },
                                    { k: "Indifferente", label: "Indifferente", color: null },
                                ].map(opt => (
                                    <button key={opt.k} type="button" data-testid={`auto-${opt.k}`} onClick={() => setPreferenzaAuto(opt.k)} className={`px-4 py-2 border uppercase tracking-widest text-xs transition-colors duration-150 ${preferenzaAuto === opt.k ? (opt.color ? `border-[${opt.color}] bg-[${opt.color}]/10 text-[#F4F2ED]` : "border-[#E2231A] bg-[#E2231A]/10 text-[#F4F2ED]") : "border-[#9AA2AD]/30 text-[#9AA2AD] hover:text-[#F4F2ED]"}`} style={preferenzaAuto === opt.k && opt.color ? { borderColor: opt.color, backgroundColor: `${opt.color}1A` } : {}}>{opt.label}</button>
                                ))}
                            </div>
                            <p className="text-[10px] uppercase tracking-widest text-[#9AA2AD]/70 mt-2">Assegnazione soggetta a disponibilità.</p>
                        </div>
                    )}

                    <label className="flex items-center gap-3 text-sm cursor-pointer mb-3">
                        <Checkbox data-testid="check-fattura" checked={wantsInvoice} onCheckedChange={v => setWantsInvoice(!!v)} className="border-[#9AA2AD]/50 data-[state=checked]:bg-[#E2231A] data-[state=checked]:border-[#E2231A] rounded-none" />
                        <span>Richiedo fattura (P.IVA / SDI)</span>
                    </label>

                    {wantsInvoice && (
                        <div className="grid md:grid-cols-2 gap-3 mb-5">
                            <input value={invoice.ragione_sociale} onChange={e => setInvoice(i => ({ ...i, ragione_sociale: e.target.value }))} placeholder="Ragione sociale" className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 text-sm md:col-span-2" />
                            <input data-testid="input-piva" value={invoice.partita_iva} onChange={e => setInvoice(i => ({ ...i, partita_iva: e.target.value }))} placeholder="Partita IVA" className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 text-sm" />
                            <input value={invoice.codice_sdi} onChange={e => setInvoice(i => ({ ...i, codice_sdi: e.target.value.toUpperCase() }))} placeholder="Codice SDI (7 char)" maxLength={7} className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 uppercase text-sm" />
                            <input value={invoice.pec} onChange={e => setInvoice(i => ({ ...i, pec: e.target.value }))} placeholder="PEC (in alternativa al SDI)" className="bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 text-sm md:col-span-2" />
                        </div>
                    )}

                    {/* WhatsApp marketing consent — separato da GDPR e visivamente accattivante */}
                    <label
                        data-testid="whatsapp-marketing-box"
                        className={`block mb-4 border-l-4 ${checks.whatsapp_marketing ? "border-[#25D366] bg-[#25D366]/10" : "border-[#25D366]/60 bg-[#25D366]/5"} p-4 cursor-pointer transition-colors duration-150`}
                    >
                        <div className="flex items-start gap-3">
                            <Checkbox
                                data-testid="check-whatsapp-marketing"
                                checked={checks.whatsapp_marketing}
                                onCheckedChange={(v) => setChecks(c => ({ ...c, whatsapp_marketing: !!v }))}
                                className="mt-0.5 border-[#25D366]/70 data-[state=checked]:bg-[#25D366] data-[state=checked]:border-[#25D366] rounded-none"
                            />
                            <div className="flex-1">
                                <div className="font-display text-base uppercase tracking-widest text-[#F4F2ED] mb-1">🏎️ Sì, restiamo in contatto su WhatsApp</div>
                                <p className="text-xs text-[#F4F2ED]/85 leading-relaxed">
                                    Ricevo su WhatsApp <b>aggiornamenti sulla mia prenotazione</b> (turno, orari, ritrovo, meteo, comunicazioni last-minute) e in futuro <b>anteprime esclusive</b> su nuovi eventi VELORA JDM, offerte riservate e inviti privati per gli appassionati.
                                </p>
                                <p className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mt-2">
                                    Consenso <b>separato</b> dalla Privacy Policy · revocabile in qualsiasi momento scrivendo a privacy@velorajdm.com.
                                </p>
                            </div>
                        </div>
                    </label>

                    <div className="space-y-3 mb-4">
                        {[
                            ["maggiorenne", "Confermo di essere maggiorenne."],
                            ["patente", "Confermo di avere una patente di guida valida."],
                            ["idoneita", "Confermo la mia idoneità fisica alla guida sportiva."],
                            ["gdpr", "Acconsento al trattamento dei dati personali (Privacy Policy GDPR)."],
                            ["immagini", "Acconsento all'uso di foto/video (opzionale)."],
                        ].map(([k, l]) => (
                            <label key={k} className="flex items-start gap-3 text-sm cursor-pointer">
                                <Checkbox data-testid={`check-${k}`} checked={checks[k]} onCheckedChange={(v) => setChecks(c => ({ ...c, [k]: !!v }))} className="mt-0.5 border-[#9AA2AD]/50 data-[state=checked]:bg-[#E2231A] data-[state=checked]:border-[#E2231A] rounded-none" />
                                <span>{l}</span>
                            </label>
                        ))}
                    </div>

                    <div className="mb-4">
                        <div className="text-xs uppercase tracking-[0.3em] text-[#9AA2AD] mb-2 flex items-center gap-2"><PenTool size={14} strokeWidth={1.5} /> Firma</div>

                        {/* Toggle firma digitale / autografa */}
                        <div className="grid grid-cols-2 gap-2 mb-3" role="tablist">
                            <button
                                type="button"
                                data-testid="sig-mode-digital"
                                onClick={() => setSignMode("digital")}
                                className={`text-xs uppercase tracking-widest py-3 px-3 border transition-colors duration-150 ${signMode === "digital" ? "border-[#E2231A] bg-[#E2231A]/10 text-[#F4F2ED]" : "border-[#9AA2AD]/30 text-[#9AA2AD] hover:border-[#9AA2AD]"}`}
                            >⚡ Firma con 1 click<br/><span className="text-[9px] normal-case tracking-normal text-[#9AA2AD]">consigliato · rapido</span></button>
                            <button
                                type="button"
                                data-testid="sig-mode-autograph"
                                onClick={() => setSignMode("autograph")}
                                className={`text-xs uppercase tracking-widest py-3 px-3 border transition-colors duration-150 ${signMode === "autograph" ? "border-[#E2231A] bg-[#E2231A]/10 text-[#F4F2ED]" : "border-[#9AA2AD]/30 text-[#9AA2AD] hover:border-[#9AA2AD]"}`}
                            >✏️ Firma con dito<br/><span className="text-[9px] normal-case tracking-normal text-[#9AA2AD]">autografa su schermo</span></button>
                        </div>

                        {signMode === "digital" ? (
                            <div data-testid="digital-signature-info" className="border border-[#E2231A]/40 bg-[#E2231A]/5 p-4">
                                <div className="text-[10px] uppercase tracking-widest text-[#E2231A] mb-2 font-display">Firma Elettronica Semplice · eIDAS art. 3.10</div>
                                <p className="text-xs text-[#F4F2ED] leading-relaxed">
                                    Cliccando <b>"Firma e completa"</b> qui sotto sottoscrivi digitalmente il documento. Vengono registrati in modo immutabile:
                                </p>
                                <ul className="text-xs text-[#9AA2AD] mt-2 space-y-0.5 pl-4 list-disc">
                                    <li>data e ora esatta della firma</li>
                                    <li>indirizzo IP e dispositivo</li>
                                    <li>codice fiscale e voucher</li>
                                    <li>hash SHA-256 del contenuto accettato</li>
                                </ul>
                                <p className="text-[10px] text-[#9AA2AD] mt-2">Valore legale: <b>Firma Elettronica Semplice</b> ex art. 20 CAD, valida per contratti di consumo standard come questo.</p>
                            </div>
                        ) : (
                            <>
                                <div className="border border-[#9AA2AD]/30 bg-[#F4F2ED]">
                                    <SignatureCanvas ref={sigRef} penColor="#15181C" canvasProps={{ width: 700, height: 180, className: "w-full", "data-testid": "signature-canvas" }} />
                                </div>
                                <button data-testid="btn-clear-signature" onClick={() => sigRef.current?.clear()} className="mt-2 text-xs uppercase tracking-widest text-[#9AA2AD] hover:text-[#E2231A] flex items-center gap-1"><RotateCcw size={12} /> Pulisci</button>
                            </>
                        )}
                        <input data-testid="signature-name" value={signatureName} onChange={e => setSignatureName(e.target.value)} placeholder="Nome per la firma" className="mt-3 w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] outline-none px-4 py-3 text-sm" />
                    </div>

                    <button data-testid="btn-submit-manleva" disabled={submitting} onClick={submit} className="w-full flex items-center justify-center gap-3 bg-[#E2231A] hover:bg-[#C11B15] disabled:bg-[#9AA2AD]/20 px-6 py-4 font-display text-xl uppercase tracking-wider transition-colors duration-150">
                        {submitting ? <Loader2 className="animate-spin" /> : <>{signMode === "digital" ? "Firma e completa" : "Invia manleva firmata"} <ArrowRight size={20} /></>}
                    </button>
                </Step>
                )}

                {step === 4 && (
                    <div data-testid="tunnel-completed" className="border border-[#E2231A] bg-[#E2231A]/10 p-8 text-center">
                        <CheckCircle2 className="mx-auto text-[#E2231A] mb-4" size={56} strokeWidth={1.5} />
                        <div className="inline-flex items-center gap-2 border border-[#E2231A] bg-[#15181C] px-3 py-1 mb-4">
                            <CheckCircle2 size={14} className="text-[#E2231A]" strokeWidth={2} />
                            <span className="font-display text-xs uppercase tracking-widest text-[#F4F2ED]">Verified & Signed</span>
                        </div>
                        <h3 className="font-display text-3xl uppercase mb-2">Prenotazione completata!</h3>
                        <p className="text-[#9AA2AD] text-sm mb-6">Riceverai una email con tutti i dettagli. Ti aspettiamo in pista.</p>
                        <div className="flex flex-wrap justify-center gap-3">
                            <button data-testid="btn-view-voucher" onClick={() => sid ? navigate(`/payment/success?session_id=${sid}`) : navigate("/")} className="border border-[#E2231A] hover:bg-[#E2231A] hover:text-[#F4F2ED] px-5 py-3 uppercase tracking-widest text-sm transition-colors duration-150">{sid ? "Torna al voucher" : "Vai alla home"}</button>
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </div>
    );
}

const Step = ({ num, title, active, done, children }) => (
    <section className={`mb-6 border ${done ? "border-[#E2231A]/60" : active ? "border-[#9AA2AD]/40" : "border-[#9AA2AD]/15"} bg-[#1C2026] p-5`}>
        <div className="flex items-baseline gap-4 mb-3">
            <span className={`font-display text-2xl ${done ? "text-[#E2231A]" : "text-[#9AA2AD]"}`}>{num}</span>
            <h2 className="font-display text-xl md:text-2xl uppercase">{title}</h2>
            {done && <CheckCircle2 size={18} className="text-[#E2231A]" />}
        </div>
        {(active || done) && children}
    </section>
);

const LegalBox = ({ tid, title, subtitle, text, fullLink, accepted, onAccept, acceptLabel }) => {
    return (
        <div data-testid={tid} className={`mb-4 border ${accepted ? "border-[#E2231A]/60" : "border-[#9AA2AD]/25"} bg-[#15181C]`}>
            <div className="px-4 py-3 border-b border-[#9AA2AD]/15 flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="font-display text-lg uppercase tracking-widest">{title}</div>
                    {subtitle && <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">{subtitle}</div>}
                </div>
                {fullLink && (
                    <a href={fullLink} target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-widest text-[#E2231A] hover:underline">Testo completo ↗</a>
                )}
            </div>
            <div
                data-testid={`${tid}-scroll`}
                className="max-h-56 md:max-h-64 overflow-y-auto text-[13px] text-[#9AA2AD] whitespace-pre-line px-4 py-4 leading-relaxed"
            >
                {text}
            </div>
            <label className="flex items-start gap-3 text-sm cursor-pointer px-4 py-3 border-t border-[#9AA2AD]/15">
                <Checkbox
                    data-testid={`${tid}-check`}
                    checked={accepted}
                    onCheckedChange={(v) => onAccept(v)}
                    className="mt-0.5 border-[#9AA2AD]/50 data-[state=checked]:bg-[#E2231A] data-[state=checked]:border-[#E2231A] rounded-none"
                />
                <span className="text-[#F4F2ED]">{acceptLabel}</span>
            </label>
        </div>
    );
};
